// Radha — import real receivables from a CSV.
//
// The path off fictional seed onto real data. Parses a CSV client-side, maps the
// common column names, previews, and bulk-inserts into collection_accounts. The
// database (033) sets the tenant from the token, masks PAN authoritatively, and
// derives stage/risk from days-past-due — so the client can't send an unmasked
// PAN or the wrong tenant. Gated to non-auditor roles by the caller.
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { collectionsKeys } from "../hooks/useCollectionsDirect";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { fmtMoneyFull } from "../lib/money";

// header alias -> canonical column
const MAP: Record<string, string> = {
  name: "debtor_name", debtor: "debtor_name", debtor_name: "debtor_name", customer: "debtor_name",
  outstanding: "outstanding_amount", outstanding_amount: "outstanding_amount", amount: "outstanding_amount", balance: "outstanding_amount",
  original: "original_amount", original_amount: "original_amount",
  dpd: "days_past_due", days_past_due: "days_past_due", "days past due": "days_past_due",
  state: "debtor_state", debtor_state: "debtor_state",
  gstin: "debtor_gstin", debtor_gstin: "debtor_gstin",
  pan: "debtor_pan_masked", debtor_pan: "debtor_pan_masked",
  msme: "is_msme", is_msme: "is_msme",
  consent: "consent_to_contact", consent_to_contact: "consent_to_contact",
};
const NUMERIC = new Set(["outstanding_amount", "original_amount", "days_past_due"]);
const BOOL = new Set(["is_msme", "consent_to_contact"]);

// small CSV parser: handles quoted fields and commas within quotes
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cur = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") {
      if (cur !== "" || row.length) { row.push(cur); rows.push(row); row = []; cur = ""; }
      if (c === "\r" && text[i + 1] === "\n") i++;
    } else cur += c;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ""));
}

type Row = Record<string, string | number | boolean>;

function toRows(csv: string): { rows: Row[]; skipped: number } {
  const grid = parseCsv(csv);
  if (grid.length < 2) return { rows: [], skipped: 0 };
  const headers = grid[0].map((h) => MAP[h.trim().toLowerCase()]).map((h) => h ?? "");
  const rows: Row[] = [];
  let skipped = 0;
  for (const line of grid.slice(1)) {
    const r: Row = {};
    headers.forEach((col, i) => {
      if (!col) return;
      const raw = (line[i] ?? "").trim();
      if (raw === "") return;
      if (NUMERIC.has(col)) r[col] = Number(raw.replace(/[₹,\s]/g, "")) || 0;
      else if (BOOL.has(col)) r[col] = /^(y|yes|true|1)$/i.test(raw);
      else r[col] = raw;
    });
    if (r.debtor_name) rows.push(r); else skipped++;
  }
  return { rows, skipped };
}

export default function ImportReceivables() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setNote(null);
    if (!f) return;
    f.text().then((t) => {
      const { rows, skipped } = toRows(t);
      setRows(rows); setSkipped(skipped);
      if (rows.length === 0) setNote({ ok: false, text: "No rows with a debtor name found — check the CSV headers." });
    });
  };

  const doImport = async () => {
    setBusy(true); setNote(null);
    try {
      const { error } = await supabase.from("collection_accounts").insert(rows);
      if (error) throw error;
      setNote({ ok: true, text: `Imported ${rows.length} account${rows.length > 1 ? "s" : ""}` });
      setRows([]); setSkipped(0);
      qc.invalidateQueries({ queryKey: collectionsKeys.accounts });
    } catch (e) {
      setNote({ ok: false, text: writeErrorMessage(e) });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "14px 16px", marginBottom: 22, background: "var(--surface-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>Import receivables</strong>
        <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>
          CSV with columns like: debtor_name, outstanding_amount, days_past_due, state, gstin, pan, msme
        </span>
        <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ fontSize: 13 }} />
        {rows.length > 0 && (
          <button onClick={doImport} disabled={busy}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 7, cursor: "pointer", fontSize: 13 }}>
            {busy ? "Importing…" : `Import ${rows.length}`}
          </button>
        )}
        {note && <span style={{ fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </div>
      {rows.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--fg-muted)" }}>
          Ready: {rows.length} rows{skipped > 0 ? ` (${skipped} skipped — no debtor name)` : ""}. PAN will be masked; stage &amp; risk set from days past due.
          <div style={{ marginTop: 6 }}>
            {rows.slice(0, 3).map((r, i) => (
              <div key={i} style={{ fontFamily: "'DM Mono', ui-monospace, monospace" }}>
                {String(r.debtor_name)} · {fmtMoneyFull(Number(r.outstanding_amount ?? 0))} · {String(r.days_past_due ?? 0)} dpd
              </div>
            ))}
            {rows.length > 3 && <div>…and {rows.length - 3} more</div>}
          </div>
        </div>
      )}
    </div>
  );
}
